/*
 * Copyright Traceloop
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// TODO: Remove unnecessary comments after all instrumentations are updated before (parent branch merge)

export const SpanAttributes = {
  // Guardrail attributes (custom — not yet in OTel core spec)

  // Parent guardrail span (gen_ai.operation.name = "guardrail.run")
  GEN_AI_GUARDRAIL_NAME: "gen_ai.guardrail.name",
  GEN_AI_GUARDRAIL_STATUS: "gen_ai.guardrail.status",
  GEN_AI_GUARDRAIL_DURATION: "gen_ai.guardrail.duration",
  GEN_AI_GUARDRAIL_GUARD_COUNT: "gen_ai.guardrail.guard_count",
  GEN_AI_GUARDRAIL_FAILED_GUARD_COUNT: "gen_ai.guardrail.failed_guard_count",

  // Child guard span (gen_ai.operation.name = "guard")
  GEN_AI_GUARDRAIL_INPUT: "gen_ai.guardrail.input",
  GEN_AI_GUARDRAIL_OUTPUT: "gen_ai.guardrail.output",
  GEN_AI_GUARDRAIL_ERROR_TYPE: "gen_ai.guardrail.error.type",
  GEN_AI_GUARDRAIL_ERROR_MESSAGE: "gen_ai.guardrail.error.message",

  // Attributes not yet in @opentelemetry/semantic-conventions

  // TODO: Remove after all instrumentations are updated
  // Kept for backwards compatibility
  GEN_AI_USAGE_CACHE_CREATION_INPUT_TOKENS:
    "gen_ai.usage.cache_creation_input_tokens",

  // TODO: Remove after all instrumentations are updated
  // Kept for backwards compatibility
  GEN_AI_USAGE_CACHE_READ_INPUT_TOKENS: "gen_ai.usage.cache_read_input_tokens",

  GEN_AI_USAGE_REASONING_TOKENS: "gen_ai.usage.reasoning_tokens",

  GEN_AI_REQUEST_THINKING_TYPE: "gen_ai.request.thinking_type",

  GEN_AI_REQUEST_THINKING_BUDGET_TOKENS:
    "gen_ai.request.thinking.budget_tokens",
  // LLM

  // TODO: Remove after all instrumentations are updated
  // Kept for backwards compatibility
  LLM_REQUEST_TYPE: "llm.request.type",

  GEN_AI_USAGE_TOTAL_TOKENS: "gen_ai.usage.total_tokens",

  // TODO: Remove after all instrumentations are updated
  // Kept for backwards compatibility
  LLM_USAGE_TOTAL_TOKENS: "llm.usage.total_tokens",

  // TODO: Remove after all instrumentations are updated
  // Kept for backwards compatibility
  LLM_TOP_K: "llm.top_k",

  // TODO: Remove after all instrumentations are updated
  // Kept for backwards compatibility
  LLM_FREQUENCY_PENALTY: "llm.frequency_penalty",

  // TODO: Remove after all instrumentations are updated
  // Kept for backwards compatibility
  LLM_PRESENCE_PENALTY: "llm.presence_penalty",

  // TODO: Remove after all instrumentations are updated
  // Kept for backwards compatibility
  LLM_CHAT_STOP_SEQUENCES: "llm.chat.stop_sequences",

  // TODO: Remove after all instrumentations are updated
  // Kept for backwards compatibility
  LLM_REQUEST_FUNCTIONS: "llm.request.functions",

  // Vector DB
  VECTOR_DB_VENDOR: "db.system",
  VECTOR_DB_QUERY_TOP_K: "db.vector.query.top_k",
  VECTOR_DB_TABLE_NAME: "db.vector.table_name",
  VECTOR_DB_ADD_COUNT: "db.vector.add.count",
  VECTOR_DB_DELETE_SELECTOR: "db.vector.delete.selector",
  VECTOR_DB_DELETE_COUNT: "db.vector.delete.count",
  VECTOR_DB_GET_SELECTOR: "db.vector.get.selector",
  VECTOR_DB_GET_COUNT: "db.vector.get.count",
  VECTOR_DB_GET_INCLUDE_METADATA: "db.vector.get.include_metadata",
  VECTOR_DB_GET_INCLUDE_VALUES: "db.vector.get.include_values",

  // LLM Workflows (all Traceloop-specific, no OTel equivalents yet)
  TRACELOOP_SPAN_KIND: "traceloop.span.kind",
  TRACELOOP_WORKFLOW_NAME: "traceloop.workflow.name",
  TRACELOOP_ENTITY_NAME: "traceloop.entity.name",
  TRACELOOP_ENTITY_PATH: "traceloop.entity.path",
  TRACELOOP_ENTITY_VERSION: "traceloop.entity.version",
  TRACELOOP_ASSOCIATION_PROPERTIES: "traceloop.association.properties",
  TRACELOOP_ENTITY_INPUT: "traceloop.entity.input",
  TRACELOOP_ENTITY_OUTPUT: "traceloop.entity.output",

  // MCP (Model Context Protocol)
  MCP_RESPONSE_VALUE: "mcp.response.value",
  MCP_REQUEST_ID: "mcp.request.id",
};

export const Events = {
  DB_QUERY_EMBEDDINGS: "db.query.embeddings",
  DB_QUERY_RESULT: "db.query.result",
};

export const EventAttributes = {
  // Query Embeddings
  DB_QUERY_EMBEDDINGS_VECTOR: "db.query.embeddings.vector",

  // Query Result (canonical format)
  DB_QUERY_RESULT_ID: "db.query.result.id",
  DB_QUERY_RESULT_SCORE: "db.query.result.score",
  DB_QUERY_RESULT_DISTANCE: "db.query.result.distance",
  DB_QUERY_RESULT_METADATA: "db.query.result.metadata",
  DB_QUERY_RESULT_VECTOR: "db.query.result.vector",
  DB_QUERY_RESULT_DOCUMENT: "db.query.result.document",

  // DEPRECATED: Vector DB Query Request
  VECTOR_DB_QUERY_TOP_K: "db.vector.query.top_k",
  VECTOR_DB_QUERY_INCLUDE_VALUES: "db.vector.query.include_values",
  VECTOR_DB_QUERY_INCLUDE_METADATA: "db.vector.query.include_metadata",
  VECTOR_DB_QUERY_ID: "db.vector.query.id",
  VECTOR_DB_QUERY_EMBEDDINGS_VECTOR: "db.vector.query.embeddings.vector",
  VECTOR_DB_QUERY_METADATA_FILTER: "db.vector.query.metadata_filter",

  // DEPRECATED: Vector DB Query Response
  VECTOR_DB_QUERY_RESULT_NAMESPACE: "db.vector.query.result.namespace",
  VECTOR_DB_QUERY_RESULT_READ_UNITS_CONSUMED:
    "db.vector.query.result.read_units",
  VECTOR_DB_QUERY_RESULT_MATCHES_LENGTH:
    "db.vector.query.result.matches_length",

  // DEPRECATED: Vector DB Query Response of each result
  VECTOR_DB_QUERY_RESULT_SCORE: "db.vector.query.result.{i}.score",
  VECTOR_DB_QUERY_RESULT_ID: "db.vector.query.result.{i}.id",
  VECTOR_DB_QUERY_RESULT_VALUES: "db.vector.query.result.{i}.values",
  VECTOR_DB_QUERY_RESULT_SPARSE_INDICES:
    "db.vector.query.result.{i}.sparse.indices",
  VECTOR_DB_QUERY_RESULT_SPARSE_VALUES:
    "db.vector.query.result.{i}.sparse.values",
  VECTOR_DB_QUERY_RESULT_METADATA: "db.vector.query.result.{i}.metadata",
};

export enum LLMRequestTypeValues {
  COMPLETION = "completion",
  CHAT = "chat",
  RERANK = "rerank",
  UNKNOWN = "unknown",
}

export enum TraceloopSpanKindValues {
  WORKFLOW = "workflow",
  TASK = "task",
  AGENT = "agent",
  TOOL = "tool",
  SESSION = "session",
  UNKNOWN = "unknown",
}

/**
 * Standardized finish reason values following OpenTelemetry semantic conventions.
 * These should be used by all LLM provider instrumentations when mapping
 * provider-specific stop reasons to standard values.
 */
export const FinishReasons = {
  STOP: "stop",
  LENGTH: "length",
  TOOL_CALL: "tool_call",
  CONTENT_FILTER: "content_filter",
  ERROR: "error",
  FINISH_REASON_UNSPECIFIED: "",
} as const;
